import { ClaimRow, match, UpdateMatchSubmatchData } from 'api/endpoints';
import { useMatchCodeClaimsQuery } from 'hooks/useMatchCodeClaims';
import _, { isEmpty } from 'lodash';
import {
  ITrampedDialog,
  SourceTypeIDs,
} from 'pages/ClaimsContractsToolPage/hooks/useMatching';
import { useSingleClaimContext } from 'pages/SingleClaimPage/providers/SingleClaimProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type ClaimMatchRow = Pick<
  ClaimRow,
  | 'referenceNumber'
  | 'counterpartyName'
  | 'currentSecured'
  | 'currentAdministrative'
  | 'currentPriority'
  | 'currentUnsecured'
  | 'currentTotal'
  | 'currentDebtor'
  | 'subMatchCode'
  | 'sourceType'
  | 'majorCategory'
  | 'minorCategory'
  | 'notMatched'
  | 'matchCode'
  | 'id'
>;

type Column = { title: string; field: keyof ClaimMatchRow };

export const COLUMNS: Column[] = [
  { title: 'Ref', field: 'referenceNumber' },
  { title: 'Counterparty Name', field: 'counterpartyName' },
  { title: 'Secured', field: 'currentSecured' },
  { title: 'Admin', field: 'currentAdministrative' },
  { title: 'Priority', field: 'currentPriority' },
  { title: 'Unsecured', field: 'currentUnsecured' },
  { title: 'Total', field: 'currentTotal' },
  { title: 'Current Debtor', field: 'currentDebtor' },
  { title: 'Major Category', field: 'majorCategory' },
  { title: 'Minor Category', field: 'minorCategory' },
  { title: 'Source Type', field: 'sourceType' },
  { title: 'Submatch Code', field: 'subMatchCode' },
];

export function useSingleClaimMatches() {
  const { claim, reloadData } = useSingleClaimContext();
  const matchedClaimsQuery = useMatchCodeClaimsQuery(claim);

  const [saving, setSaving] = useState(false);
  const [matchedClaims, setMatchedClaims] = useState<ClaimMatchRow[]>();
  const [trumpedMatchDialog, setTrumpedMatchDialog] = useState<ITrampedDialog>();

  useEffect(() => {
    const items = matchedClaimsQuery?.data?.claims.items ?? [];
    setMatchedClaims(items);
  }, [claim?.referenceNumber, matchedClaimsQuery?.data?.claims]);

  const headerClaim = useMemo(
    () => matchedClaims?.find((r) => r.referenceNumber === claim?.referenceNumber),
    [claim?.referenceNumber, matchedClaims],
  );

  const updateSubMatchCode = useCallback(
    async (data: UpdateMatchSubmatchData) => {
      setSaving(true);
      await match.updateMatchSubmatch(data);
      await matchedClaimsQuery.refetch();
      setSaving(false);
    },
    [matchedClaimsQuery],
  );

  const forceRefreshGrid = useCallback(async () => {
    //workaround: clone items to the new object - to make AgGrid table refresh table rows
    setMatchedClaims((items) => _.cloneDeep(items));
  }, []);

  const showTrumpedSubMatchDialog = useCallback(
    async (data: UpdateMatchSubmatchData, matchedClaims: string[]) => {
      const dialogParams = {
        matchedClaims,
        onAccept: async () => {
          closeTrumpedSubMatchDialog();
          await updateSubMatchCode(data);
          await reloadData();
        },
        onCancel: async () => {
          closeTrumpedSubMatchDialog();
          forceRefreshGrid();
        },
      };

      setTrumpedMatchDialog(dialogParams);
    },
    [forceRefreshGrid, reloadData, updateSubMatchCode],
  );

  const closeTrumpedSubMatchDialog = () => setTrumpedMatchDialog(undefined);

  const handleSubMatchCodeChange = useCallback(
    async (value: number, data: ClaimRow) => {
      const body = {
        referenceNumber: data.referenceNumber,
        matchCode: data.matchCode,
        subMatchCode: value,
        sourceTypeID: SourceTypeIDs[data.sourceType!],
        claimId: data.id,
        counterpartyName: data.counterpartyName,
      };
      const trumpedBody = {
        claimId: data.id,
        matchCode: data.matchCode,
        subMatchCode: value,
      };
      const matchedClaims = await match.getTrumpedSchedules(trumpedBody);

      !isEmpty(matchedClaims)
        ? showTrumpedSubMatchDialog(body, matchedClaims!)
        : updateSubMatchCode(body);
    },
    [updateSubMatchCode, showTrumpedSubMatchDialog],
  );

  return {
    headerClaim,
    matchedClaims,
    loading: matchedClaimsQuery.loading || saving,
    trumpedMatchDialog,
    handleSubMatchCodeChange,
  };
}
